// ============================================================================
// ANALYTICS EVENT TRACKING
// Handles tracking of user interactions and sends events to Shopify Analytics
// ============================================================================

// ============================================================================
// CONFIGURATION & STATE
// ============================================================================

const LOGIN_PENDING_KEY = 'login_pending';
const COOKIE_DOMAIN = '.browniespain.com';

const CONFIG = {
  isCollectionPage: window.location.pathname.includes("/collections/"),
  isProductPage: window.location.pathname.includes("/product/"),
  isBlog: window.location.pathname.includes("/blogs/"),
  isPage: window.location.pathname.includes("/pages/"),
  isCart: window.location.pathname.includes("/cart/"),
  referrer: document.referrer || window.location.href,
  currency: window.currency || "EUR",
  brand: "Brownie",
};

// ============================================================================
// USER DATA
// Every event carries `bsft_email`, the visitor's email, when it is known: the
// logged-in customer's (from head-init.liquid), or the last one typed in a
// newsletter form on this browser (remembered in localStorage, so the next
// page views carry it too). `bsft_email` is a Blueshift custom identifier and
// the consumers add the same value to every server-side event, so Blueshift
// merges the anonymous web profile with the customer's. The Shopify custom
// pixel adds it to the standard events (gtm.js).
// ============================================================================

// Previous versions kept the plain email under this same key, so it is read as
// it is.
const EMAIL_STORAGE_KEY = 'bsft_email';
// SHA-256 of the email kept by a previous version of this file. It cannot be
// turned back into the email and is just dropped.
const LEGACY_EMAIL_HASH_STORAGE_KEY = 'bsft_email_sha256';

// Same normalization as head-init.liquid, gtm.js and consumers/shared/bsftEmail.js
// (trimmed and lowercased): they must produce the same value for Blueshift to
// match them.
function normalizeEmail(email) {
  return typeof email === 'string' ? email.trim().toLowerCase() : '';
}

function readStoredEmail() {
  try {
    return normalizeEmail(localStorage.getItem(EMAIL_STORAGE_KEY));
  } catch (e) {
    return '';
  }
}

function storeEmail(email) {
  try {
    localStorage.setItem(EMAIL_STORAGE_KEY, email);
  } catch (e) {}
}

class UserProfile {
  constructor() {
    const data = window.customerData || {};
    this.isLoggedIn = data.isLoggedIn || false;
    this.id = data.id || "";
    this.ordersCount = data.ordersCount || 0;
    this.hasNewsletter = data.hasNewsletter || false;
    this.email = data.email || readStoredEmail();
    if (data.email) storeEmail(data.email);

    this.save();
  }

  save() {
    localStorage.setItem("userProfile", JSON.stringify(this.getCommonPayload()));
  }

  setEmail(email) {
    this.email = email;
    storeEmail(email);
    this.save();
  }

  getCommonPayload() {
    return {
      buyer: this.ordersCount > 0 ? "comprador" : "no comprador",
      login_session: this.isLoggedIn ? "login" : "no login",
      order_n: String(this.ordersCount),
      registered: this.isLoggedIn ? "registrado" : "no registrado",
      subscribed: this.hasNewsletter ? "suscrito" : "no suscrito",
      custom_client_id: this.id,
      userGroups: this.isLoggedIn ? "cliente" : "visitante",
      userId: this.getGaCookie(),
      userLogged: this.isLoggedIn ? "1" : "0",
      referrerCrosseling: CONFIG.referrer,
      ...(this.email && { bsft_email: this.email }),
    };
  }

  getGaCookie() {
    const cookies = document.cookie;
    const cookieArray = cookies.split(";");

    for (let cookie of cookieArray) {
      cookie = cookie.trim();

      if (cookie.startsWith("_ga")) {
        return cookie.substring(4);
      }
    }
    return null;
  }
}

const userProfile = new UserProfile();

try {
  localStorage.removeItem(LEGACY_EMAIL_HASH_STORAGE_KEY);
} catch (e) {}

// ============================================================================
// ANALYTICS PUBLISHER
// ============================================================================

class AnalyticsPublisher {
  static publish(eventName, data) {
    const payload = {
      ...data,
      ...userProfile.getCommonPayload(),
    };
    const prefix = "brownie:";
    Shopify.analytics.publish(prefix + eventName, payload);
  }
}

// ============================================================================
// UTILITIES
// ============================================================================

function getCollectionName() {
  const match = window.location.pathname.match(/\/collections\/([^\/]+)/);
  return match?.[1] || null;
}


// ============================================================================
// EVENT HANDLERS
// ============================================================================

function handleGridViewChange(viewType) {
  AnalyticsPublisher.publish("click_element", {
    event: "click_element",
    element_category: "vista",
    element_detail: viewType,
  });
}

function handleFilterButtonClick() {
  AnalyticsPublisher.publish("click_element", {
    event: "click_element",
    element_category: "filtro",
    element_detail: ''
  });
}

function handleCompleteYourLookClick(productName) {
  AnalyticsPublisher.publish("click_element", {
    event: "click_element",
    element_category: "completa_look",
    element_detail: productName
  });
}

function handleFilterApplied(keys, values) {
  AnalyticsPublisher.publish("filtrar", {
    event: "filtrar",
    element_category: keys,
    element_detail: values,
  });
}

function handleCollectionProductCardClick(itemElement, collectionName, index) {
  const title =
    itemElement.querySelector(".brn-card__title")?.textContent.trim() || "";
  const priceText =
    itemElement.querySelector(".price-item")?.textContent || "0";
  const price = priceText.replace("€", "").trim();
  const productId = itemElement.getAttribute("data-variant-id") || "";
  const productVariant = itemElement.getAttribute("data-variant") || "";

  console.log(productId)
  AnalyticsPublisher.publish("select_item", {
    event: "ee-productClick",
    ecommerce: {
      click: {
        actionField: { list: collectionName },
        items: [
          {
            name: title,
            id: productId,
            price: price,
            category: collectionName,
            variant: productVariant,
            position: index + 1,
          },
        ],
      },
    },
  });
}

function handleProductShare(detail) {
  AnalyticsPublisher.publish("product_interaction", {
    event: "product_interaction",
    element_category: "compartir",
    element_detail: detail,
  });
}

function handleMySizeClicked() {
  AnalyticsPublisher.publish("product_interaction", {
    event: "mi_talla",
  });
}

function handleSimilarProductsClicked(detail) {
  AnalyticsPublisher.publish("product_interaction", {
    event: "product_interaction",
    element_category: "ver_similares",
    element_detail: detail,
  });
}

function handleIWantItClicked(detail) {
  AnalyticsPublisher.publish("product_interaction", {
    event: "product_interaction",
    element_category: "lo_quiero",
    element_detail: detail,
  });
}

function handleAvisameClicked() {
  AnalyticsPublisher.publish("product_interaction", {
    event: "product_interaction",
    element_category: "avisame",
  });
}

function handleSizeGuideClicked(productId) {
  AnalyticsPublisher.publish('product_interaction', { event: 'mi_talla', product_id: productId })
}

function handleVariantClicked(category, detail, productId) {
  AnalyticsPublisher.publish('product_interaction',
    {
      event: 'product_interaction',
      element_category: category,
      element_detail: detail,
      product_id: productId
    })
}

function handleWishlistAdd(event) {
  let category;
  if (CONFIG.isCollectionPage) {
    category = "COLLECTION";
  } else if (CONFIG.isProductPage) {
    category = "PDP";
  } else if (CONFIG.isCart) {
    category = "CART";
  } else {
    category = "PAGE";
  }

  console.log(event)
  AnalyticsPublisher.publish("add_to_wishlist", {
    event: "add_to_wishlist",
    currency: CONFIG.currency,
    price: parseFloat(event.detail.productPrice, 10) / 100,
    item_brand: CONFIG.brand,
    item_category: "",
    item_id: event.detail.variantId,
    item_name: event.detail.productTitle,
    quantity: 1,
    eventAction: 'ux-addToWishlist',
    eventCategory: category,
    eventLabel: event.detail.productTitle,
    wishlist_n: 1,
    items: [
      {
        id: event.detail.variantId,
        price: parseFloat(event.detail.productPrice, 10) / 100,
        category: '',
        variant: event.detail.productVariant
      }
    ]
  });
}

function handleWishlistRemove(event) {
  let category;
  if (CONFIG.isCollectionPage) {
    category = "COLLECTION";
  } else if (CONFIG.isProductPage) {
    category = "PDP";
  } else if (CONFIG.isCart) {
    category = "CART";
  } else {
    category = "PAGE";
  }
  AnalyticsPublisher.publish("remove_from_wishlist", {
    event: "remove_from_wishlist",
    item_id: event.detail.variantId,
    custom_item_id: "",
    item_brand: CONFIG.brand,
    item_list_name: category,
    item_variant: event.detail.variantTitle,
  });
}

function handlePromotionClicked({ name, img, position, slot, id }) {
  const payload = {
    event: "ee-promoClick",
    ecommerce: {
      promoClick: {
        promotions: [
          {
            id: id,
            name: name,
            creative: img,
            position: position,
            slot: slot,
          },
        ],
      },
    },
  };

  AnalyticsPublisher.publish("select_promotion", payload);
}

function popupView(category) {
  AnalyticsPublisher.publish('view_popup', {
    event: 'view_popup',
    element_category: 'promotion - ' + category,
    element_detail: 'view'
  })

}

function popupClicked({ category, detail, text }) {
  AnalyticsPublisher.publish('click_popup', {
    event: 'click_popup',
    element_category: category,
    element_detail: detail,
    element_text: text
  })

}

function handleLead(email) {
  if (email) handleIdentify(email);
  AnalyticsPublisher.publish('lead', { event: 'lead' })
}

// Called by every form where the visitor types their own email (newsletter
// forms). Publishes an `identify` event right away, since the form may be the
// last thing they do on the site.
function handleIdentify(email) {
  const value = normalizeEmail(email);
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return;

  if (value === userProfile.email) return;
  userProfile.setEmail(value);
  AnalyticsPublisher.publish('identify', { event: 'identify' });
}


function handleHeaderIcon(category) {
  AnalyticsPublisher.publish('click_header', {
    event: 'click_header',
    element_category: category
  })
}

function handleLoginStarted() {

  if (window.customer !== '') return;

  AnalyticsPublisher.publish('login_started', {
    event: 'login_started'
  })
}

function handleMenuItem(text) {
  AnalyticsPublisher.publish('click_menu', {
    event: 'click_menu',
    element_text: text
  })
}

// ============================================================================
// EVENT LISTENERS INITIALIZATION
// ============================================================================

function initializeFilterButton() {
  const filterBtn = document.querySelector(".collection__filter-trigger");
  if (filterBtn) {
    filterBtn.addEventListener("click", handleFilterButtonClick);
  }
}

function initializeVistaChange() {
  const vistaBtn = document.querySelector('.grid-toggle__icon');
  const vistaGridFour = document.getElementById('iconGrid4');

  if (!vistaBtn) return;

  vistaBtn.addEventListener('click', () => {
    if (vistaGridFour.checkVisibility()) {
      handleGridViewChange('2');
    } else {
      handleGridViewChange('4');
    }
  })
}

function initializeCompleteYourLookClick() {
  const completeYourLook = document.querySelectorAll('#completeLookCarousel .brn-product__card');

  completeYourLook.forEach((card) => {
    card.addEventListener('click', () => {
      const favButton = card.querySelector('.brn-product__fav');
      const name = favButton ? favButton.getAttribute('data-title') : '';
      handleCompleteYourLookClick(name);
    });
  });
}

function initializePromotionBanner() {
  const banners = document.querySelectorAll("image-banner");
  const mediaQuery = window.matchMedia("(min-width: 768px)");

  for (let i = 0; i < banners.length; i++) {
    const banner = banners[i];
    banner.addEventListener("click", () => {
      const sectionEl = banner.closest("section");
      const id = (sectionEl?.dataset.sectionId || sectionEl.id) ?? null;

      const mobileImg = banner.querySelector(
        ".hero__pic--mobile img"
      ).currentSrc;
      const desktopImg = banner.querySelector(
        ".hero__pic--desktop img"
      ).currentSrc;
      const name = banner.querySelector(".hero__title")?.textContent?.trim() || '';
      const img = mediaQuery ? desktopImg : mobileImg;

      let position;
      if (CONFIG.isCollectionPage) {
        position = "COLLECTION";
      } else if (CONFIG.isProductPage) {
        position = "PRODUCT";
      } else if (CONFIG.isBlog) {
        position = "BLOG";
      } else if (CONFIG.isPage) {
        position = "PAGE";
      } else {
        position = "HOME";
      }

      position = position + `-0${i + 1}`;

      handlePromotionClicked({ id, name, img, position, slot: `hero${i + 1}` });
    });
  }
}

function initializeFooterEvents() {
  document.addEventListener("click", function (event) {
    const link = event.target.closest("a");
    if (!link) return;
    const footer = link.closest(".shopify-section-group-footer-group");
    if (!footer) return;

    const eventAction =
      link.textContent?.trim() || link.getAttribute("aria-label") || link.href;

    const payload = {
      event: "ux-footer",
      eventCategory: "footer",
      eventAction,
    };

    AnalyticsPublisher.publish("footer", payload);
  });
}

function initializeWishlistEvents() {
  document.addEventListener("wishlist:productAdded", handleWishlistAdd);
  document.addEventListener("wishlist:productRemoved", handleWishlistRemove);
}

function initializeCollectionPageTracking() {
  if (!CONFIG.isCollectionPage) return;

  const collectionName = getCollectionName();
  const items = document.querySelectorAll(".collection__grid__item");

  items.forEach((item, index) => {
    item.addEventListener("click", () => {
      handleCollectionProductCardClick(item, collectionName, index);
    });
  });
}

function initializeSizeGuideEvent() {
  const sizeGuideBtn = document.querySelector('.size-guide-btn');
  if (!sizeGuideBtn) return;
  sizeGuideBtn.addEventListener('click', () => {
    const productId = sizeGuideBtn.dataset.productId
    handleSizeGuideClicked(productId);
  })
}

function initializeProductVariantsEvents() {
  const variantBtns = document.querySelectorAll('variant-options .f-button__input')

  variantBtns.forEach(elem => {
    elem.addEventListener('click', () => {
      const category = elem.getAttribute('name')?.replace(/-mobile$/, '');
      const detail = elem.getAttribute('value');
      const productId = new URLSearchParams(window.location.search).get('variant');
      handleVariantClicked(category, detail, productId);
    })
  })
}

function initializePopups() {
  const popups = document.querySelectorAll('.custom-popup__content');
  if (!popups) return;

  popups.forEach(elem => {
    const title = elem.querySelector('.custom-popup__title')?.textContent?.trim() || undefined;

    title ? popupView(title) : popupView('promotion_image');

    elem.addEventListener('click', (e) => {
      const text = elem.querySelector('.custom-popup__text')?.textContent?.trim() || '';

      let content;
      let category;
      if (!text && !title) {
        content = 'promotion_image'
        category = 'promotion_image'
      } else {
        content = title + ' - ' + text
        category = 'promotion'
      }

      if (e.target.closest('.custom-popup__close')) {
        popupClicked({
          detail: 'close',
          category: category,
          text: content,
        });
      }

      if (e.target.closest('.btn')) {
        popupClicked({
          detail: 'leave',
          category: category,
          text: content,
        });
      }
    })

  })
}

function initializeHeader() {
  const accountIcon = document.querySelector('.account-icon');
  const cartIcon = document.getElementById('cart-icon-bubble');

  if (accountIcon) {
    accountIcon.addEventListener('click', () => {
      handleHeaderIcon('usuario');
      handleLoginStarted();
    })
  }

  if (cartIcon) {
    cartIcon.addEventListener('click', () => {
      handleHeaderIcon('cesta');
    })
  }

}

function initializaMenuItems() {

  const menuLinks = document.querySelectorAll('.brn-menu__link');

  menuLinks.forEach(elem => {
    elem.addEventListener('click', () => {
      const text = elem.textContent?.trim();
      if (text) handleMenuItem(text);
    })
  })
}

function handleQuickbuyClick(productTitle) {
  AnalyticsPublisher.publish('click_element', {
    event: 'click_element',
    element_category: 'quickbuy',
    element_detail: productTitle,
  });
}

function handleShopTheLook(step, action) {
  AnalyticsPublisher.publish('shop_the_look', {
    event: 'shop_the_look',
    stl_step: step,
    stl_action: action,
  });
}

function initializeShopTheLook() {
  document.addEventListener('click', (e) => {
    const target = e.target;
    if (!target || typeof target.closest !== 'function') return;

    let step = null;
    let action = null;

    if (target.closest('.complete-look-btn')) {
      step = '1';
      action = 'crear_look';
    } else if (target.closest('.look-drawer__continue')) {
      step = '2';
      action = 'continue';
    } else if (target.closest('.look-drawer-root .add__to__cart, .look-drawer-root .variant-elem[aria-available="true"]')) {
      // .variant-elem: desktop adds from the size buttons of the hover panel
      // (.add__to__cart--desktop), which don't carry the .add__to__cart class.
      step = '3';
      action = 'add_to_cart';
    } else if (target.closest('.look-drawer-root .brn-product__fav')) {
      step = '3';
      action = 'wishlist';
    }

    if (!action) return;

    handleShopTheLook(step, action);
  });
}

// ============================================================================
// INITIALIZATION
// ============================================================================

function initialize() {
  initializeVistaChange();
  initializeFilterButton();
  initializeCompleteYourLookClick();
  initializeWishlistEvents();
  initializeCollectionPageTracking();
  initializePromotionBanner();
  initializeFooterEvents();
  initializeSizeGuideEvent();
  initializeProductVariantsEvents();
  initializePopups();
  initializeHeader();
  initializaMenuItems();
  initializeShopTheLook();
}

initialize();

// ============================================================================
// PUBLIC API
// ============================================================================

window.analyticsUtils = {
  pushGridViewEventToDataLayer: handleGridViewChange,
  pushFilteredValuesToDataLayer: handleFilterApplied,
  handleProductShare: handleProductShare,
  handleMySizeClicked: handleMySizeClicked,
  handleSimilarProductsClicked: handleSimilarProductsClicked,
  handleIWantItClicked: handleIWantItClicked,
  handleAvisameClicked: handleAvisameClicked,
  handleLead: handleLead,
  identify: handleIdentify,
  handleQuickbuyClick: handleQuickbuyClick,
};
